import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import cn from 'classnames';

import { useDispatch, useSelector } from 'store';
import { fetchBalanceData, selectBalance } from 'store/balanceReducer';
import { selectActiveCompany, selectCompanies } from 'store/companiesReducer';
import { selectUser } from 'store/usersReducer';
import { removeTokenWithRedirect } from 'utils/token';
import ModalBalanceRefill from 'components/Modals/ModalBalanceRefill';
import ModalChangePassword from 'components/Modals/ModalChangePassword';
import ModalConfirm from 'components/Modals/ModalConfirm';
import { ReactComponent as LogoIcon } from 'assets/imgs/rt-logo.svg';
import { ReactComponent as WalletIcon } from 'assets/imgs/wallet.svg';
import { ReactComponent as ProfileIcon } from 'assets/imgs/profile.svg';
import { ReactComponent as CaretIcon } from 'assets/imgs/caret-down.svg';
import classes from './header.module.scss';

const Header: React.FC = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const [openedMenu, setOpenedMenu] = useState('');
	const [modalOpen, setModalOpen] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const user = useSelector(selectUser);
	const balance = useSelector(selectBalance);
	const companies = useSelector(selectCompanies);
	const activeCompany = useSelector(selectActiveCompany);
	const activeCompanyId = activeCompany?.id || '';
	const activeCompanyName = activeCompany?.name || '';
	const profileTitle = `${user?.firstName || ''} ${user?.lastName || ''}`;
	const logoUrl =
		companies.length > 1 || !activeCompanyId
			? '/companies'
			: `${activeCompanyId}/products`;
	const isBalanceMenuOpen = openedMenu === 'balance';
	const isProfileMenuOpen = openedMenu === 'profile';

	const handleToggleMenu =
		(type: string) => (e: React.MouseEvent<HTMLDivElement>) => {
			e.stopPropagation();
			setOpenedMenu(openedMenu === type ? '' : type);
		};

	const handleLogout = (e: React.MouseEvent<HTMLDivElement>) => {
		e.stopPropagation();
		setModalOpen('logout');
	};

	const handleBalanceRefill = (e: React.MouseEvent<HTMLDivElement>) => {
		e.stopPropagation();
		setModalOpen('balanceRefill');
	};

	const handleChangePassword = (e: React.MouseEvent<HTMLDivElement>) => {
		e.stopPropagation();
		setModalOpen('password');
	};

	const handleCloseModal = () => {
		setModalOpen('');
	};

	const handleConfirmLogout = () => {
		removeTokenWithRedirect();
	};

	const handleOutsideClick = () => {
		setOpenedMenu('');
	};

	const handleRefreshBalance = async () => {
		if (isLoading) return;

		setIsLoading(true);
		try {
			await dispatch(fetchBalanceData()).unwrap();
		} catch (err) {
			console.log(err);
		}
		setIsLoading(false);
	};

	useEffect(() => {
		document.addEventListener('click', handleOutsideClick);
		return () => {
			document.removeEventListener('click', handleOutsideClick);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getLinkClassNames = (path: string) => {
		const url = location.pathname || '';
		const isActive = url.startsWith(path);
		return cn(classes.link, { [classes.link__active]: isActive });
	};

	const leftLinks = (
		<div className={classes.left}>
			<Link className={cn(classes.link, classes.link__logo)} to={logoUrl}>
				<LogoIcon className={classes.iconLogo} />
			</Link>
			<Link
				className={getLinkClassNames(`/${activeCompanyId}/products`)}
				to={`/${activeCompanyId}/products`}
			>
				Рекламные инструменты
			</Link>
			<Link className={getLinkClassNames('/companies')} to="/companies">
				Организация
				{activeCompanyName && (
					<span className={classes.companyName}>
						«{activeCompanyName}»
					</span>
				)}
			</Link>
		</div>
	);

	const rightLinks = (
		<div className={classes.right}>
			<div
				className={classes.balance}
				onClick={handleToggleMenu('balance')}
			>
				<WalletIcon className={classes.icon} />
				<div className={classes.balanceTitle}>
					Баланс{` ${balance}`} руб.
				</div>
				<CaretIcon
					className={cn(classes.iconCaret, {
						[classes.iconCaret__open]: isBalanceMenuOpen,
					})}
				/>
				{isBalanceMenuOpen && (
					<div className={classes.menu}>
						<div
							className={classes.menuLink}
							onClick={handleBalanceRefill}
						>
							Пополнить баланс
						</div>
						<Link
							className={classes.menuLink}
							to="/balance/history"
						>
							История операций
						</Link>
						<div
							className={classes.menuLink}
							onClick={handleRefreshBalance}
						>
							Обновить баланс
						</div>
					</div>
				)}
			</div>
			<div
				className={classes.profile}
				onClick={handleToggleMenu('profile')}
			>
				<ProfileIcon className={classes.icon} />
				<div className={classes.profileTitle}>{profileTitle}</div>
				<CaretIcon
					className={cn(classes.iconCaret, {
						[classes.iconCaret__open]: isProfileMenuOpen,
					})}
				/>
				{isProfileMenuOpen && (
					<div className={classes.menu}>
						<Link className={classes.menuLink} to="/profile">
							Редактировать профиль
						</Link>
						<div
							className={classes.menuLink}
							onClick={handleChangePassword}
						>
							Изменить пароль
						</div>
						<div
							className={classes.menuLink}
							onClick={handleLogout}
						>
							Выйти
						</div>
					</div>
				)}
			</div>
		</div>
	);

	return (
		<div className={classes.header}>
			{leftLinks}
			{rightLinks}
			<ModalConfirm
				className={classes.modal}
				isOpen={modalOpen === 'logout'}
				onClose={handleCloseModal}
				onConfirm={handleConfirmLogout}
				title="Выход"
				text="Вы действительно хотите выйти из личного кабинета?"
			/>
			<ModalBalanceRefill
				className={classes.modal}
				isOpen={modalOpen === 'balanceRefill'}
				onClose={handleCloseModal}
			/>
			<ModalChangePassword
				className={classes.modal}
				isOpen={modalOpen === 'password'}
				onClose={handleCloseModal}
			/>
		</div>
	);
};

export default Header;
